import API from '../Api/api';

const CITY_ALIASES = {
  bangalore: 'bengaluru',
  bengaluru: 'bengaluru',
  madras: 'chennai',
  trichy: 'trichy',
  tiruchirappalli: 'trichy',
  tiruchirapalli: 'trichy',
  thiruvananthapuram: 'thiruvananthapuram',
  trivandrum: 'thiruvananthapuram',
  visakhapatnam: 'visakhapatnam',
  vizag: 'visakhapatnam',
  mumbai: 'mumbai',
  bombay: 'mumbai',
  pune: 'pune',
  hyderabad: 'hyderabad',
  kochi: 'kochi',
  cochin: 'kochi',
  mysore: 'mysuru',
  mysuru: 'mysuru',
};

const CITY_CENTROIDS = {
  chennai: [13.0827, 80.2707],
  madurai: [9.9252, 78.1198],
  coimbatore: [11.0168, 76.9558],
  trichy: [10.7905, 78.7047],
  bengaluru: [12.9716, 77.5946],
  mysuru: [12.2958, 76.6394],
  kochi: [9.9312, 76.2673],
  thiruvananthapuram: [8.5241, 76.9366],
  visakhapatnam: [17.6868, 83.2185],
  vijayawada: [16.5062, 80.6480],
  hyderabad: [17.3850, 78.4867],
  mumbai: [19.0760, 72.8777],
  pune: [18.5204, 73.8567],
};

const EARTH_RADIUS_KM = 6371;
const toRad = (deg) => (deg * Math.PI) / 180;

function haversineKm(lat1, lon1, lat2, lon2) {
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function cleanCityToken(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/\s+district$/i, '')
    .replace(/\s+urban$/i, '')
    .replace(/\s+division$/i, '')
    .trim();
}

function canonicalCityToken(name) {
  const cleaned = cleanCityToken(name);
  return CITY_ALIASES[cleaned] || cleaned;
}

function cleanCityLabel(name) {
  const raw = String(name || '').trim();
  if (!raw) return '';
  return raw
    .replace(/\s+District$/i, '')
    .replace(/\s+Urban$/i, '')
    .replace(/\s+Division$/i, '')
    .trim();
}

function extractCityFromNominatimAddress(addr = {}) {
  const primary = [addr.city, addr.town, addr.municipality];
  for (const raw of primary) {
    const cleaned = cleanCityLabel(raw);
    if (cleaned.length >= 2) return cleaned;
  }

  const secondary = [addr.city_district, addr.village, addr.locality];
  for (const raw of secondary) {
    const cleaned = cleanCityLabel(raw);
    if (cleaned.length >= 2) return cleaned;
  }

  const tertiary = [addr.state_district, addr.district, addr.county, addr.suburb];
  for (const raw of tertiary) {
    const cleaned = cleanCityLabel(raw);
    if (cleaned.length >= 2) return cleaned;
  }
  return '';
}

/** Match app city record by geocoded city name. */
export function findCityByName(cityName, cities = []) {
  if (!cityName || !Array.isArray(cities) || !cities.length) return null;
  const target = canonicalCityToken(cityName);
  return cities.find((c) => {
    const label = canonicalCityToken(c.city_name || c.city);
    return label === target || label.startsWith(target) || target.startsWith(label);
  }) || null;
}

/** Find a catalog city mentioned in a geocoded address string. */
export function findCityInAddress(displayName, cities = []) {
  if (!displayName || !cities.length) return null;
  const parts = String(displayName).split(',').map((s) => s.trim()).filter(Boolean);
  const haystack = parts.map((p) => canonicalCityToken(p)).join('|');

  for (const city of cities) {
    const label = canonicalCityToken(city.city_name || city.city);
    if (!label) continue;
    if (haystack.includes(label)) return city;
    for (const part of parts) {
      const token = canonicalCityToken(part);
      if (token === label || token.startsWith(label) || label.startsWith(token)) {
        return city;
      }
    }
  }
  return null;
}

function findNearestCatalogCity(lat, lng, cities = [], maxKm = 150) {
  let best = null;
  let bestDist = Infinity;
  for (const city of cities) {
    const key = canonicalCityToken(city.city_name);
    const centroid = CITY_CENTROIDS[key];
    if (!centroid) continue;
    const dist = haversineKm(lat, lng, centroid[0], centroid[1]);
    if (dist < bestDist) {
      bestDist = dist;
      best = city;
    }
  }
  return bestDist <= maxKm ? best : null;
}

/** Map coordinates + geocoded label to the closest catalog city record. */
export function resolveCatalogCity(lat, lng, geocodedCity = '', cities = []) {
  const lt = Number(lat);
  const lg = Number(lng);
  const label = cleanCityLabel(geocodedCity);

  let matched = label ? findCityByName(label, cities) : null;
  if (!matched && label) {
    matched = findCityInAddress(label, cities);
  }

  if (matched) {
    return {
      city: matched.city_name,
      cityId: matched.id ?? null,
    };
  }

  if (Number.isFinite(lt) && Number.isFinite(lg)) {
    const nearest = findNearestCatalogCity(lt, lg, cities);
    if (nearest) {
      return {
        city: label || nearest.city_name,
        cityId: nearest.id ?? null,
      };
    }
  }

  return {
    city: label,
    cityId: null,
  };
}

export function resolveCatalogCityId({ cityName, cityId, lat, lng, cities = [] }) {
  if (cityId) {
    const byId = cities.find((c) => Number(c.id) === Number(cityId));
    if (byId?.id) return Number(byId.id);
  }
  const resolved = resolveCatalogCity(lat, lng, cityName, cities);
  return resolved.cityId ?? null;
}

export function resolveDisplayCity({ cityName, cityId, lat, lng, cities = [] }) {
  if (cityId) {
    const byId = cities.find((c) => Number(c.id) === Number(cityId));
    if (byId?.city_name) return byId.city_name;
  }
  const resolved = resolveCatalogCity(lat, lng, cityName, cities);
  return resolved.city || cleanCityLabel(cityName) || '';
}

async function fetchNominatimDirect(lat, lng) {
  const lt = Number(lat);
  const lg = Number(lng);
  const url = `https://nominatim.openstreetmap.org/reverse?lat=${lt}&lon=${lg}&format=json&addressdetails=1&zoom=10&countrycodes=in`;
  const res = await fetch(url, {
    headers: {
      'Accept-Language': 'en',
      'User-Agent': 'PropertiesInfinite-Web/1.0',
    },
  });
  if (!res.ok) return null;
  return res.json();
}

async function reverseGeocodeDetailsLocal(lat, lng, cities = []) {
  const lt = Number(lat);
  const lg = Number(lng);
  if (!Number.isFinite(lt) || !Number.isFinite(lg)) {
    return { displayName: '', city: '', cityId: null };
  }

  let displayName = `${lt.toFixed(4)}, ${lg.toFixed(4)}`;
  let city = '';

  try {
    const data = await fetchNominatimDirect(lt, lg);
    if (data) {
      displayName = data.display_name || displayName;
      city = extractCityFromNominatimAddress(data.address || {});
      if (!city) {
        const fromText = findCityInAddress(displayName, cities);
        if (fromText) city = fromText.city_name;
      }
    }
  } catch {
    /* use fallbacks below */
  }

  const resolved = resolveCatalogCity(lt, lg, city, cities);
  return {
    displayName,
    city: resolved.city,
    cityId: resolved.cityId,
  };
}

/** Reverse geocode coordinates — prefers backend API, falls back to direct Nominatim. */
export async function reverseGeocodeDetails(lat, lng, cities = []) {
  const lt = Number(lat);
  const lg = Number(lng);
  if (!Number.isFinite(lt) || !Number.isFinite(lg)) {
    return { displayName: '', city: '', cityId: null };
  }

  try {
    const res = await API.get(`/geocode/reverse?lat=${lt}&lng=${lg}`);
    const data = res.data || {};
    const geocodedCity = String(data.city || '').trim();
    const resolved = resolveCatalogCity(lt, lg, geocodedCity, cities);
    return {
      displayName: data.displayName || data.display_name || `${lt.toFixed(4)}, ${lg.toFixed(4)}`,
      city: resolved.city || geocodedCity,
      cityId: data.city_id ?? data.cityId ?? resolved.cityId ?? null,
    };
  } catch {
    return reverseGeocodeDetailsLocal(lt, lg, cities);
  }
}

export async function reverseGeocode(lat, lng, cities = []) {
  const { displayName } = await reverseGeocodeDetails(lat, lng, cities);
  return displayName;
}

export async function resolveLocationDetails(lat, lng, existing = {}, cities = []) {
  const lt = Number(lat);
  const lg = Number(lng);
  const details = await reverseGeocodeDetails(lt, lg, cities);

  const resolved = resolveCatalogCity(
    lt,
    lg,
    details.city || existing.city || '',
    cities,
  );

  const name = existing.name?.trim() || details.displayName;
  return {
    name,
    city: resolved.city || cleanCityLabel(details.city) || cleanCityLabel(existing.city) || '',
    displayName: details.displayName,
    cityId: resolved.cityId ?? details.cityId ?? existing.cityId ?? null,
  };
}

export async function resolveLocationName(lat, lng, existingName = '', cities = []) {
  if (existingName?.trim()) return existingName.trim();
  const { displayName } = await reverseGeocodeDetails(lat, lng, cities);
  return displayName;
}
